import React from 'react';
import { H1 } from '../../../components/basic/Text';
import { useMessageGetter } from 'react-message-context';
import { Body2, Button, Checkbox, H5Style } from '../../../components/common';
import styled from 'styled-components/macro';
import Card from '../../../components/common/Card';
import { useKennoTenantState } from '../../../context/kennoTenantContext';
import { Form } from '../../layoutComponents';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { InputField } from '../../../components/common/forms';
import { DropdownOptionValue } from '../../../components/common/Dropdown';
import { FormContainer } from '../../forms/layoutComponents';
import { zodResolver } from '@hookform/resolvers/zod';
import { TerminationInstructionsFormState, terminationInstructionsSchema } from './schema';
import { DeviceSize } from '../../../components/basic/DeviceSize';
import { FlexRow } from '../../../components/basic/Flex';

const Container = styled.div`
  max-width: 800px;
  margin-bottom: 30px;
`;

const AllResidentsTitle = styled.h2`
  font-size: 21px;
`;

const ResidentTitle = styled.h3`
  ${H5Style}
`;

const ResidentList = styled.ul`
  list-style: none;
  padding: 0;
  font-family: ${(props) => props.theme.text.primaryFont};
`;

const InstructionsTitle = styled.h2`
  font-size: 24px;
  margin-top: 60px;

  @media ${DeviceSize.mobileL} {
    font-size: 21px;
  }
`;

const InstructionsList = styled.ol`
  margin: 40px 0 70px 0;
  li {
    margin-bottom: 30px;
  }
`;

const ListItemTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
`;

const ContinueButton = styled(Button)`
  margin-top: 30px;
`;

type Props = {
  afterSubmit?: () => void;
  isContinueDisabled: boolean;
};

export const TerminationInstructions: React.FC<Props> = ({ afterSubmit, isContinueDisabled }) => {
  const translationPrefix = 'termination';
  const t = useMessageGetter(translationPrefix);
  const { contract } = useKennoTenantState();
  const formMethods = useForm<TerminationInstructionsFormState>({
    resolver: zodResolver(terminationInstructionsSchema),
  });

  const contractTenants = contract.data?.contractTenants;

  const onSubmit: SubmitHandler<TerminationInstructionsFormState> = () => {
    afterSubmit?.();
  };

  return (
    <FormProvider {...formMethods}>
      <FormContainer>
        <Container>
          <H1>{t('title')}</H1>
          <Body2 margin={'40px 0'}>{t('description')}</Body2>
          <Body2>{t('description2')}</Body2>
          <Card>
            <AllResidentsTitle>{t('residents.title')}</AllResidentsTitle>
            <ResidentTitle>{t('residents.withContract')}</ResidentTitle>
            <ResidentList>
              {contractTenants && contractTenants.contractParties.length > 0 ? (
                contractTenants.contractParties.map((tenant, index) => (
                  <li key={index}>{`${tenant.firstName} ${tenant.lastName}`}</li>
                ))
              ) : (
                <li>-</li>
              )}
            </ResidentList>
            <ResidentTitle>{t('residents.other')}</ResidentTitle>
            <ResidentList>
              {contractTenants && contractTenants.otherParties.length > 0 ? (
                contractTenants.otherParties.map((tenant, index) => (
                  <li key={index}>{`${tenant.firstName} ${tenant.lastName}`}</li>
                ))
              ) : (
                <li>-</li>
              )}
            </ResidentList>
          </Card>

          <InstructionsTitle>{t('instructions.title')}</InstructionsTitle>
          <InstructionsList>
            <li>
              <ListItemTitle>{t('instructions.step1.title')}</ListItemTitle>
              <Body2>{t('instructions.step1.description')}</Body2>
            </li>
            <li>
              <ListItemTitle>{t('instructions.step2.title')}</ListItemTitle>
              <Body2>{t('instructions.step2.description1')}</Body2>
              <Body2>{t('instructions.step2.description2')}</Body2>
            </li>
            <li>
              <ListItemTitle>{t('instructions.step3.title')}</ListItemTitle>
              <Body2>{t('instructions.step3.description')}</Body2>
            </li>
            <li>
              <ListItemTitle>{t('instructions.step4.title')}</ListItemTitle>
              <Body2>{t('instructions.step4.description')}</Body2>
            </li>
            <li>
              <ListItemTitle>{t('instructions.step5.title')}</ListItemTitle>
              <Body2>{t('instructions.step5.description')}</Body2>
            </li>
          </InstructionsList>
          <Form onSubmit={formMethods.handleSubmit(onSubmit)}>
            {!isContinueDisabled && (
              <>
                <InputField<TerminationInstructionsFormState, DropdownOptionValue>
                  fieldPath="instructionsRead"
                  translationPrefix={translationPrefix}
                  hideLabel
                  inputElementRenderer={({ value, id, onChange }) => (
                    <FlexRow gap="14px">
                      <Checkbox value={!!value} id={id} onChange={onChange} />
                      <div>{`${t('instructionsRead')} `}</div>
                    </FlexRow>
                  )}
                />
                <ContinueButton primary iconRight icon={'arrowForward'} type="submit">
                  {t('continue')}
                </ContinueButton>
              </>
            )}
          </Form>
        </Container>
      </FormContainer>
    </FormProvider>
  );
};
